import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import jwt from "jwt-decode";
import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Col, Row } from "react-bootstrap";

import { ReactComponent as LogoPuc } from "../assets/images/logo_pucpr_horizontal_rgb 1.svg";
import DialogMessage from "../components/DialogMessage";
import { SCOPES } from "../config/authConfig";
import Context from "../context/Context";
import DialogMessageProps from "../model/DialogMessageProps";
import { GenericValues } from "../model/dto/GenericValues";
import { User } from "../model/dto/User";
import UserService from "../service/Docente/UserService";
import ButtonStyles from "../styles/components/ButtonStyles";
import {
    StyledCardLogin,
    StyledContainer,
    StyledTitle,
} from "../styles/msal/SignInStyles";
import { StyledBacklogPhrase } from "../styles/pages/Schedule/ScheduleStyle";
import GenericUtils from "../utils/GenericUtils";

const { BtnPrimary } = ButtonStyles;

interface SignInProps {
    setAutenticado: React.Dispatch<React.SetStateAction<boolean>>;
}

function SignIn({ setAutenticado }: SignInProps) {
    const { instance, inProgress } = useMsal();
    const { setValidToken } = useContext(Context);
    const processingRef = useRef(false);

    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);

    const getUser = async (userCode: string) => {
        const response = await UserService.getUser(userCode, instance);

        if (GenericUtils.isSuccessStatus(response.status)) {
            const matriculaAdp =
                response.data?.docente?.matriculaAdp ??
                response.data?.matriculaAdp;

            if (!matriculaAdp) {
                setValidToken(true);
                localStorage.clear();
                setDialogMessageProps({
                    show: true,
                    type: "error",
                    title: "Erro",
                    text: "Não foi possível obter a matrícula do docente. Verifique seu cadastro.",
                } as DialogMessageProps);
                return;
            }

            const resp = await UserService.getUserWithMatriculaAdp(
                matriculaAdp,
                instance
            );
            const { items } = resp.data as GenericValues<User>;

            const informacoes = {
                ...response.data,
                isDac: Boolean(
                    items?.[0].perfis.find(
                        (perfil: any) => perfil.idPerfil === 1
                    )
                ),
                isDocente: Boolean(
                    items?.[0].perfis.find(
                        (perfil: any) => perfil.idPerfil === 3
                    )
                ),
                isCord: Boolean(
                    items?.[0].perfis.find(
                        (perfil: any) => perfil.idPerfil === 4
                    )
                ),
                isJuri: Boolean(
                    items?.[0].perfis.find(
                        (perfil: any) => perfil.idPerfil === 2
                    )
                ),
            };
            localStorage.setItem("user", JSON.stringify(informacoes));
            localStorage.setItem("isLocalAuthenticated", JSON.stringify(true));
            setAutenticado(true);
            GenericUtils.getTokenMicroservices(instance);
        } else {
            setValidToken(false);
            localStorage.clear();
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleRedirectResult = useCallback(async () => {
        try {
            const response = await instance.handleRedirectPromise();

            if (response && !processingRef.current) {
                processingRef.current = true;

                localStorage.setItem(
                    "accessTokenDocente",
                    response.accessToken
                );
                localStorage.setItem(
                    "account",
                    JSON.stringify(response.account)
                );

                const userJwt: any = jwt(response.accessToken);
                getUser(userJwt?.cod_adp || userJwt?.cod_fornecedor);
            }
        } catch (error) {
            console.error("Erro no processamento do Redirect:", error);
            localStorage.clear();
        }
    }, [instance]);

    useEffect(() => {
        if (inProgress === InteractionStatus.None) {
            handleRedirectResult();
        }
    }, [inProgress, handleRedirectResult]);

    const handleLogin = () => {
        localStorage.clear();
        instance
            .loginRedirect({
                scopes: [SCOPES.DOCENTE],
            })
            .catch((e: any) => {
                console.error("Erro ao iniciar redirect:", e);
            });
    };

    const handleCloseDialogMessage = () => {
        setValidToken(false);
        setDialogMessageProps({} as DialogMessageProps);
        GenericUtils.logout(instance);
    };

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={handleCloseDialogMessage}
        />
    );

    return (
        <>
            <StyledContainer>
                <StyledCardLogin>
                    <Row>
                        <Col style={{ textAlign: "center" }}>
                            <LogoPuc style={{ width: 300, height: 120 }} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <StyledTitle>
                                Acesse aqui a Agenda Docente
                            </StyledTitle>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <BtnPrimary
                                onClick={handleLogin}
                                style={{
                                    width: "100%",
                                    height: "50px",
                                }}
                                disabled={inProgress !== InteractionStatus.None}
                            >
                                {inProgress !== InteractionStatus.None
                                    ? "Carregando..."
                                    : "Acessar"}
                            </BtnPrimary>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            style={{
                                textAlign: "center",
                                marginTop: "15px",
                            }}
                        >
                            <StyledBacklogPhrase>
                                Precisa de ajuda?
                                <a
                                    style={{
                                        paddingLeft: "5px",
                                        color: "#8a0538",
                                    }}
                                    href="https://app.pipefy.com/public/form/2Skyfues"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Acesse aqui.
                                </a>
                            </StyledBacklogPhrase>
                        </Col>
                    </Row>

                    <Row>
                        <Col
                            style={{
                                textAlign: "center",
                                marginTop: "15px",
                            }}
                        >
                            <StyledBacklogPhrase>
                                Acesse aqui nosso
                                <a
                                    style={{
                                        paddingLeft: "5px",
                                        color: "#8a0538",
                                    }}
                                    href="/aviso-de-privacidade"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Aviso de privacidade.
                                </a>
                            </StyledBacklogPhrase>
                        </Col>
                    </Row>
                </StyledCardLogin>
            </StyledContainer>
            {dialogMessage}
        </>
    );
}

export default SignIn;
